import { Box, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import styled from '@mui/material/styles/styled';
import { animated, useSpring } from '@react-spring/web';
import { get } from 'lodash-es';
import { PropTypes } from 'prop-types';
import React from 'react';
import Particles from 'react-tsparticles';
import useStore from '../../stores/store';
import { useGetCurrentUserLevel } from '../../stores/userActions';
import getQualifiedImageUrl from '../../utils/imageUrlProcessor';
import StyledTypography from '../Styled/styledParagraphTypography';
import TimelineBar from '../Timeline/timelineBar';

const StyledParticlesWrapper = styled('div')(() => ({
  position: 'absolute',
  width: 500,
  height: 300,
}));

const StyledCenterText = styled(Typography)(() => ({
  textAlign: 'center',
}));

const ImpactScoreTextContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginTop: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const TimelineWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  margin: theme.spacing(3, 0),
  padding: theme.spacing(0, 5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 1),
  },
}));

const animationOptions = {
  emitters: {
    life: {
      count: 1,
      duration: 0.1,
      delay: 0.4,
    },
    position: {
      x: 50,
      y: 100,
    },
    rate: {
      quantity: 10,
      delay: 0.15,
    },
  },
  fullScreen: {
    enable: false,
  },
  particles: {
    life: {
      duration: {
        sync: true,
        value: 5,
      },
      count: 1,
    },
    move: {
      decay: 0.05,
      direction: 'top',
      enable: true,
      gravity: {
        enable: true,
      },
      outModes: {
        top: 'none',
        default: 'destroy',
      },
      speed: {
        min: 10,
        max: 30,
      },
    },
    number: {
      value: 0,
    },
    opacity: {
      value: 1,
    },
    rotate: {
      value: {
        min: 0,
        max: 360,
      },
      direction: 'random',
      animation: {
        enable: true,
        speed: 30,
      },
    },
    tilt: {
      direction: 'random',
      enable: true,
      value: {
        min: 0,
        max: 360,
      },
      animation: {
        enable: true,
        speed: 30,
      },
    },
    size: {
      value: 3,
      animation: {
        enable: true,
        startValue: 'min',
        count: 1,
        speed: 16,
        sync: true,
      },
    },
    roll: {
      darken: {
        enable: true,
        value: 25,
      },
      enlighten: {
        enable: true,
        value: 25,
      },
      enable: true,
      speed: {
        min: 5,
        max: 15,
      },
    },
    wobble: {
      distance: 30,
      enable: true,
      speed: {
        min: -7,
        max: 7,
      },
    },
    shape: {
      type: 'image',
      options: {
        image: [
          {
            src: 'assets/images/neki-heart.png',
            width: 32,
            height: 32,
            particles: {
              size: {
                value: 16,
              },
            },
          },
        ],
      },
    },
  },
};

function ActivitySuccess(props) {
  const { header = null, subheader = null, reward = null } = props;
  const badge = get(reward, 'badges.badge', null);
  const pointsEarned = get(reward, 'points.earned', 0);
  const levelInfo = useStore(
    (state) => state.user.profile?.activeCommunity?.reward?.levels
  );
  const currentUserLevel = useGetCurrentUserLevel();
  const userNekiPoints = useStore((state) => state.user.profile.points);
  const badgesCount = useStore((state) => state.user.profile.badgesCount);

  const initial = 0;
  const destination = 360;

  const spring = useSpring({
    from: {
      rotate: initial,
      transform: `rotateY(${initial}deg)`,
    },
    to: {
      rotate: destination,
      transform: `rotateY(${destination}deg)`,
    },
    config: {
      mass: 25,
      tension: 50,
      friction: 50,
    },
  });

  return (
    <Stack alignItems="center">
      {header && (
        <Typography variant="h5" color="primary" fontWeight={500}>
          {header}
        </Typography>
      )}
      {subheader && (
        <StyledCenterText variant="h6" fontWeight={500}>
          {subheader}
        </StyledCenterText>
      )}
      <StyledParticlesWrapper>
        <Particles id="tsparticles" options={animationOptions} />
      </StyledParticlesWrapper>
      <animated.div style={spring}>
        <img
          src={
            badge
              ? getQualifiedImageUrl(badge?.imageUrl)
              : '/assets/images/star.png'
          }
          alt="badge"
          width="100"
        />
      </animated.div>
      <Stack alignItems="center" width="100%">
        {pointsEarned > 0 && (
          <StyledCenterText variant="body1" fontWeight={400} component="div">
            You have earned
            <div>
              <b>{pointsEarned} Neki points</b>
            </div>
            {badge && (
              <>
                <div>and a</div>
                <div>
                  <b>{badge.name} badge!</b>
                </div>
              </>
            )}
          </StyledCenterText>
        )}
        <TimelineWrapper>
          <TimelineBar
            items={levelInfo}
            prevValue={userNekiPoints - pointsEarned}
            value={userNekiPoints}
          />
        </TimelineWrapper>
      </Stack>
      <Divider className="w-100">
        <Typography variant="caption" fontWeight={500}>
          Here are the perks of {currentUserLevel?.name}
        </Typography>
      </Divider>
      <Box padding="8px 0">
        <StyledTypography variant="body2">
          {currentUserLevel?.description}
        </StyledTypography>
      </Box>
    </Stack>
  );
}

export default ActivitySuccess;
